/**
 * Single source of truth for status presentation on the frontend.
 * Labels, tones, badge styles, grouping predicates, and shared domain
 * constants. Dashboard, Inbox, ItemDetail, and Claims all import from
 * here — never define status mappings locally (that's how drift starts).
 */

/* Mirrors backend/src/models/constants.js — keep in sync by hand. */
export const TEMPORARY_UNLISTED_CATEGORY = 'TEMPORARY UNLISTED';

/* ---------------- labels ---------------- */
export const STATUS_LABEL = {
  // report / claim lifecycle
  SUBMITTED: 'New',
  VIEWED: 'Viewed',
  PENDING: 'New',            // legacy claim value
  CONFIRMED: 'Confirmed',
  REJECTED: 'Rejected',
  DISMISSED: 'Dismissed',    // legacy
  EXPIRED: 'Expired',
  CONTACT_SHARED: 'Contact shared',
  ACKNOWLEDGED: 'Confirmed', // legacy report value
  // item lifecycle
  ACTIVE: 'Active',
  LOST: 'Lost',
  FOUND: 'Found',
  IN_RECOVERY: 'In recovery',
  RECOVERED: 'Recovered',
  ARCHIVED: 'Archived',
};

export const statusLabel = (status) => STATUS_LABEL[status] ?? status;

/* ---------------- tones (inbox pill colors) ---------------- */
export function statusTone(status) {
  if (['SUBMITTED', 'PENDING'].includes(status)) return 'new';
  if (status === 'VIEWED') return 'viewed';
  if (['CONFIRMED', 'IN_RECOVERY', 'ACKNOWLEDGED'].includes(status)) return 'active';
  if (status === 'RECOVERED') return 'done';
  if (['REJECTED', 'DISMISSED', 'EXPIRED', 'ARCHIVED'].includes(status)) return 'closed';
  return 'new';
}

/** Tone → CSS class (see `.pill` + `.tone-*` in app.css). */
export const TONE_CLASSES = {
  new:    'tone-new',
  viewed: 'tone-viewed',
  active: 'tone-active',
  done:   'tone-done',
  closed: 'tone-closed',
};

/* ------------- legacy CSS badge class mapping -------------
 * The app.css `status status-<X>` classes are keyed to ITEM statuses.
 * When showing a CLAIM/REPORT status with those classes, map it to the
 * closest item-status class. (Claims.jsx used to inline this ternary.)
 */
export function statusBadgeClass(status) {
  const map = {
    CONFIRMED: 'RECOVERED',
    ACKNOWLEDGED: 'RECOVERED',
    REJECTED: 'ARCHIVED',
    DISMISSED: 'ARCHIVED',
    EXPIRED: 'ARCHIVED',
    SUBMITTED: 'FOUND',
    PENDING: 'FOUND',
    VIEWED: 'FOUND',
  };
  return `status status-${map[status] ?? status}`;
}

/* ---------------- grouping predicates ---------------- */
/** Item statuses that count toward the Dashboard "In recovery" stat. */
export const IN_RECOVERY_ITEM_STATUSES = ['LOST', 'FOUND', 'IN_RECOVERY'];

/** Inbox entries the owner can still act on (identify / confirm / reject). */
export const ACTIONABLE_STATUSES = ['SUBMITTED', 'VIEWED', 'PENDING'];

/* ---------------- report rejection reasons ---------------- */
export const REASON_OPTIONS = [
  { value: 'NOT_THEIRS', label: "This isn't mine" },
  { value: 'NOT_WANTED', label: 'I no longer want this item' },
  { value: 'KEEP_IT',    label: 'Tell them to keep it' },
];
export const REASON_LABEL = Object.fromEntries(REASON_OPTIONS.map((o) => [o.value, o.label]));
